//@flow

import {useState, useEffect, useCallback} from 'react';

import {useRavenAccount} from '.';
import {
  getConversationMessagesService,
  sendMessageService,
} from '../promises/message';
import {useSelector} from 'react-redux';
import {useActions} from '../../../../utils/globalStore';

import {actions} from '../duck';

import type {
  MessageRequest,
  MessageType,
  RepliedMessageRequest,
  ConversationMessage,
} from '../../../../entities';
import type {RootState} from '../../../';
import {
  processAndUploadImage,
  uploadImageService,
} from '../../../file-storage/promises';
import {useCurrentConversation} from './conversation';
import {formatTime, isWithinTimeLimit} from '../../../../utils/date';

import {endpoints} from '../api';

export type SendMessageParams = {
  message?: string,
  files?: File[] | Blob[],
  messageType?: MessageType,
  attachment?: string,
  repliedMessage?: RepliedMessageRequest,
};
const messageSelector = (state: RootState) => state.chat.conversationMessage;
const nextTokenArchivedSelector = (state: RootState) =>
  state.chat.nextTokenArchived;

const prevTokenArchivedSelector = (state: RootState) =>
  state.chat.prevTokenArchived;

const SIZE = 20;
export function useConversationMessages(
  conversationId: string,
  loadDefault?: boolean = true
) {
  const messages = useSelector(messageSelector);
  const setMessages = useActions(actions.setConversationMessages);
  const setReactionMessages = useActions(
    actions.setConversationReactionMessages
  );
  const addReactionMessage = useActions(actions.addConversationReactionMessage);
  const addOldMessages = useActions(actions.addOldMessages);
  const addNewMessages = useActions(actions.addNewMessages);
  const addArchivedMessages = useActions(actions.addArchivedMessages);
  const setNextTokenArchived = useActions(actions.setNextTokenArchived);
  const setPrevTokenArchived = useActions(actions.setPrevTokenArchived);
  const resetNextTokenArchived = useActions(actions.resetNextTokenArchived);
  const resetPrevTokenArchived = useActions(actions.resetPrevTokenArchived);

  const [loading, setLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const [error, setError] = useState(false);
  const [size, setSize] = useState();
  const [prevToken, setPrevToken] = useState('');
  const [nextToken, setNextToken] = useState('');

  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const {ravenAccount} = useRavenAccount();

  useEffect(() => {
    if (loadDefault === false) return;
    if (!ravenAccount || !conversationId) return;

    setLoading(true);

    getConversationMessagesService(conversationId, {
      size: SIZE,
    })
      .then(res => {
        console.log({res});
        setMessages(res.messages);
        setReactionMessages(res.reactions || []);
        setSize(res.size);
        setPrevToken(res.prevToken);
        setNextToken(res.nextToken);
        setHasMoreMessages(res.prevToken ? true : false);
      })
      .catch(err => {
        console.error({err});
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      console.log('Cleaning up conversation messages');
      setMessages([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, ravenAccount, setMessages, setReactionMessages]);

  const loadPrevPage = () => {
    if (!prevToken || loading || !conversationId) return;

    const shouldFetchNextPage = !loading && prevToken;

    if (!shouldFetchNextPage) return;

    setLoading(true);

    getConversationMessagesService(conversationId, {
      size,
      prevToken,
    })
      .then(res => {
        addOldMessages(res.messages);
        if (res.reactions.length > 0) {
          addReactionMessage(res.reactions);
        }
        setSize(res.size);
        setPrevToken(res.prevToken);
        setHasMoreMessages(!!res.prevToken);
      })
      .catch(err => {
        console.log({err});
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadNextPage = () => {
    if (!nextToken || loading) return;
    setLoading(true);

    getConversationMessagesService(conversationId, {
      size,
      nextToken,
    })
      .then(res => {
        addNewMessages(res.messages);
        if (res.reactions?.length) addReactionMessage(res.reactions);
        setNextToken(res.nextToken || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const sendMessage = async ({
    files,
    message,
    messageType,
    attachment,
    repliedMessage,
  }: SendMessageParams) => {
    if (!conversationId) return;
    setIsSendingMessage(true);

    if (!files && !message && !attachment) {
      setError(true);
      setIsSendingMessage(false);
      return;
    }

    try {
      const newMessage: MessageRequest = {
        text: message,
        messageType: messageType || 'TEXT',
      };

      if (repliedMessage) {
        newMessage.repliedMessage = {
          ...repliedMessage,
        };
      }

      if (files && files.length > 0) {
        if (messageType === 'IMAGE') {
          const file = files[0];
          const image = new File([file], 'image.png', {
            type: 'image/png',
          });

          const sizeThreshold = 1024 * 1024;

          const [fileResult] = await processAndUploadImage(
            image,
            'im/crm-conversation',
            {
              uploadParams: {
                temp: true,
              },
              compressParams: {
                customFormat: 'jpeg',
                quality: 0.8,
                threshold: sizeThreshold,
              },
            }
          );
          newMessage.fileUrl = fileResult.url;
        } else {
          const [fileResult] = await uploadImageService(
            files,
            'im/crm-conversation',
            {
              temp: true,
            }
          );
          newMessage.fileUrl = fileResult.url;
        }
      }

      if (attachment) {
        newMessage.fileUrl = attachment;
      }

      await sendMessageService(newMessage, conversationId);
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const addPageArchivedChat = (
    conversationId: string,
    nextTokenArchivedNew?: string,
    isFirstLoad?: boolean = false
  ) => {
    if (!isFirstLoad) {
      if (!nextTokenArchivedNew || loading) return;
    }
    setLoading(true);

    getConversationMessagesService(conversationId, {
      size: SIZE,
      nextToken: nextTokenArchivedNew || undefined,
    })
      .then(res => {
        const updatedData = res.messages.map(item => ({
          ...item,
          isArchived: true,
        }));

        addArchivedMessages(updatedData);
        setSize(res.size);

        if (res.prevToken) {
          setPrevTokenArchived(res.prevToken);
        } else {
          resetPrevTokenArchived();
        }

        if (res.nextToken) {
          setNextTokenArchived(res.nextToken);
        } else {
          resetNextTokenArchived();
        }
      })
      .catch(err => {
        console.log({err});
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const reactToMessage = useCallback(
    async (message: ConversationMessage, reaction: string) => {
      if (!conversationId) return;

      try {
        await sendMessageService(
          {
            messageType: 'REACTION',
            text: reaction,
            repliedMessage: {
              messageType: message.messageType,
              text: message.text,
              messageId: message.id,
              messageReceivedAt: message.rawReceivedAt,
              fromMe: message.fromMe,
            },
          },
          conversationId
        );
      } catch (error) {
        console.error(error);
        setError(true);
      }
    },
    [conversationId]
  );

  const navigateToToken = async (token: string) => {
    if (!conversationId || loading || !token) return;
    setLoading(true);

    try {
      const nextRes = await getConversationMessagesService(conversationId, {
        size,
        nextToken: token,
      });
      const nextMsgs = nextRes.messages;
      const newNextToken = nextRes.nextToken;

      let prevMsgs: ConversationMessage[] = [];
      let newPrevToken = '';
      if (nextMsgs.length) {
        const firstRaw = nextMsgs[0].rawReceivedAt;
        const prevRes = await getConversationMessagesService(conversationId, {
          size,
          prevToken: firstRaw,
        });
        prevMsgs = prevRes.messages;
        newPrevToken = prevRes.prevToken;
        setReactionMessages([
          ...(prevRes.reactions || []),
          ...(nextRes.reactions || []),
        ]);
      } else {
        setReactionMessages(nextRes.reactions || []);
      }

      setMessages([...prevMsgs, ...nextMsgs]);
      setPrevToken(newPrevToken);
      setNextToken(newNextToken);
      setHasMoreMessages(!!newPrevToken);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    messages,
    loading,
    loadPrevPage,
    loadNextPage,
    sendMessage,
    addPageArchivedChat,
    navigateToToken,
    error,
    reactToMessage,
    isSendingMessage,
    hasMoreMessages,
    hasMoreNext: !!nextToken,
  };
}

type AvailableConversation = {
  isAvailable: boolean,
  restTime: string,
  percentage: number,
};

const SERVICES_WITHOUT_TIME_RESTRICTION = [
  'FB_MESSENGER',
  'INSTAGRAM',
  'WHATSAPP_API',
];

export function useConversationAvailableStatus(): AvailableConversation | null {
  const {currentConversation} = useCurrentConversation();

  const [
    availableConversation,
    setAvailableConversation,
  ] = useState<AvailableConversation | null>(null);

  useEffect(() => {
    if (!currentConversation) return;

    const serviceHasTimeRestriction = SERVICES_WITHOUT_TIME_RESTRICTION.includes(
      currentConversation.ravenService
    );
    const META_CONVERSATION_AVAILABLE_HOURS = 24;

    if (!serviceHasTimeRestriction) return;
    const {
      lastMessageAt,
      lastMessageReceivedAt,
      ravenService,
    } = currentConversation;

    if (!lastMessageAt && !lastMessageReceivedAt) return;

    const checkAvailability = () => {
      const lastMessageDate = new Date(
        ravenService === 'WHATSAPP_API' ? lastMessageReceivedAt : lastMessageAt
      );

      const result = isWithinTimeLimit(
        lastMessageDate,
        META_CONVERSATION_AVAILABLE_HOURS,
        'hour'
      );

      setAvailableConversation({
        isAvailable: result.withinLimit,
        restTime: formatTime(result.timeRemaining),
        percentage: result.percentageRemaining,
      });
    };

    checkAvailability();
    const intervalId = setInterval(checkAvailability, 60000);

    return () => {
      clearInterval(intervalId);
      setAvailableConversation(null);
    };
  }, [currentConversation]);

  return availableConversation;
}

export function useNextTokenArchived() {
  const nextTokenArchived = useSelector(nextTokenArchivedSelector);
  const setNextTokenArchived = useActions(actions.setNextTokenArchived);
  const resetNextTokenArchived = useActions(actions.resetNextTokenArchived);
  const prevTokenArchived = useSelector(prevTokenArchivedSelector);
  const setPrevTokenArchived = useActions(actions.setPrevTokenArchived);

  const handleNextTokenArchived = (nextToken: string) => {
    setNextTokenArchived(nextToken);
  };

  const handleResetNextTokenArchived = useCallback(() => {
    resetNextTokenArchived();
  }, [resetNextTokenArchived]);

  const handlePrevTokenArchived = (prevToken: string) => {
    setPrevTokenArchived(prevToken);
  };

  const handleResetPrevTokenArchived = useCallback(() => {
    setPrevTokenArchived('');
  }, [setPrevTokenArchived]);

  return {
    nextTokenArchived,
    handleNextTokenArchived,
    handleResetNextTokenArchived,
    prevTokenArchived,
    handlePrevTokenArchived,
    handleResetPrevTokenArchived,
  };
}

export async function fetchWhatsappApiMedia(deviceId: string, mediaId: string) {
  const url =
    endpoints.GET_WHATSAPP_API_MEDIA +
    `?device_id=${deviceId}&media_id=${mediaId}`;
  const response = await fetch(url);
  if (!response.ok) {
    console.error('Error fetching media', response);
    throw new Error('Error fetching media');
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  return blobUrl;
}

const reactionMessagesSelector = (state: RootState) =>
  state.chat.reactionMessages;

export function useMessageReactions() {
  const reactionMessages = useSelector(reactionMessagesSelector);
  const setReactionMessages = useActions(
    actions.setConversationReactionMessages
  );
  const addReactionMessage = useActions(actions.addConversationReactionMessage);
  const removeReactionMessage = useActions(
    actions.removeConversationReactionMessage
  );

  const resetReactionMessages = useCallback(() => {
    setReactionMessages([]);
  }, [setReactionMessages]);

  return {
    reactionMessages,
    setReactionMessages,
    addReactionMessage,
    resetReactionMessages,
    removeReactionMessage,
  };
}
